import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { RecentSearchesService } from 'src/app/services/recent-searches/recent-searches.service';
import { RestService } from 'src/app/services/rest/rest.service';
import { IMAGE_ORIGIN, imageWidthUrl, webpImageUrl } from 'src/app/utils/image-url';

/**
 * Un bloque de resultados del buscador.
 *
 * Antes esto estaba escrito como literal de objeto dentro del propio `@Input()`
 * (`{ results: [], total: 0, loading: false }`). Como TIPO eso no significa "una lista",
 * significa **tupla vacía**, y `total: 0` significa el literal `0`: con strictTemplates,
 * acceder a cualquier elemento daba `Property 'name' does not exist on type 'never'`.
 * Eran 26 de los 58 errores del proyecto, todos del mismo origen.
 */
interface SearchBucket {
  results: any[];
  total: number;
  loading: boolean;
}

interface SearchSuggestions {
  categories: SearchBucket;
  deals: SearchBucket;
  stores: SearchBucket;
  users: SearchBucket;
}

@Component({
    selector: 'app-top-searches',
    templateUrl: './top-searches.component.html',
    styleUrls: ['./top-searches.component.scss'],
    standalone: false
})
export class TopSearchesComponent implements OnInit {
  private rest = inject(RestService);
  private recientes = inject(RecentSearchesService);

  readonly searchTerm = input<string>(undefined);

  readonly suggestions = input<SearchSuggestions>(undefined);

  // Era `mobile: true` (el literal, no el tipo): cualquier booleano que llegara del padre
  // daba "Type 'boolean' is not assignable to type 'true'".
  readonly mobile = input<boolean>(false);

  readonly topSearchInput = input<any[]>([], { alias: 'topSearch' });

  readonly closeBox = output<void>();

  /** Las categorías más buscadas: llegan tras un await, así que van en señal. */
  readonly topSearch = signal<any[]>([]);

  /** La lista vive en el servicio, compartida con el buscador. */
  readonly recentSearches = this.recientes.list;

  ngOnInit() {
    this.recientes.load();
    this.topSearch.set(this.topSearchInput());

    if (this.topSearch().length === 0) this.getTopSearch();
  }

  async getTopSearch() {
    try {
      // 'search/top' se movió en el backend (ahora /search/history/top y con otro shape);
      // las "Categorías más buscadas" (slug/name/image_url) viven en /search/categories/.
      const result = await this.rest.getCategoriasMasBuscadas(10);
      if (result.ok && this.topSearch().length === 0) {
        this.topSearch.set([...result.categories.results]);
      }
    } catch {
      // Silencioso: si la API de tendencias falla dejamos la lista vacía, sin
      // ensuciar la consola (best-practices Lighthouse: errors-in-console).
      this.topSearch.set([]);
    }
  }

  deleteHistory(event: Event) {
    event.stopPropagation();
    return this.recientes.clear();
  }

  deleteRow(event: Event, name: string) {
    event.stopPropagation();
    return this.recientes.remove(name);
  }

  encodeUrl(url: string) {
    return encodeURIComponent(url);
  }




  /** 40 px CSS thumbnail: one WebP variant per screen density (the API sends 60 px). */
  miniaturaSrc(url: string): string {
    return IMAGE_ORIGIN.test(url) ? webpImageUrl(imageWidthUrl(url, 80)) : url;
  }

  miniaturaSrcset(url: string): string | null {
    if (!IMAGE_ORIGIN.test(url)) return null;
    const webp = webpImageUrl(url);
    // Fotos apaisadas (258×135) en una caja cuadrada de 40 px: el ancho pedido tiene que dar
    // ALTO suficiente para cada densidad (80/160/240 de ancho → 41/83/125 px de alto).
    return `${imageWidthUrl(webp, 80)} 1x, ${imageWidthUrl(webp, 160)} 2x, ${imageWidthUrl(webp, 240)} 3x`;
  }
}
